import { NetworkType, useIntl } from '@capsicum/common';
import { Alert, ListCard, NetworkIcon, RadioButtonInput, type RadioButtonInputProps } from '@capsicum/common-web';
import { AlertIcon, ChilizIcon } from '@capsicum/common-web/icons';
import { type ReceiveNetworks } from '@capsicum/features/wallet/shared';
import styled, { useTheme } from 'styled-components';

import { NetworkMinimum } from './NetworkMinimum';

export type NetworkCardItem = {
	isMinAmountLoading?: boolean;
	isNotSupported?: boolean;
	label?: string;
	minAmount?: string;
	name: NetworkType;
	shortCode?: string;
};

export type NetworkSelectionListCardProps = {
	alert?: NetworkType;
	alertText?: string;
	item: NetworkCardItem;
	selectNetwork: (networkId: string) => void;
	status: string;
};

export const NetworkSelectionListCard = ({
	alert,
	alertText,
	item,
	selectNetwork,
	status
}: NetworkSelectionListCardProps) => {
	const { $t } = useIntl();
	const { colors } = useTheme();

	const showAlert: boolean = item.name === alert && status === 'selected';

	const radioButtonProps: RadioButtonInputProps = {
		checked: status === 'selected',
		disabled: item.isNotSupported,
		onPress: () => {
			selectNetwork(item.name);
		}
	};

	const selectNetworkHandler = () => {
		!item.isNotSupported && selectNetwork(item.name);
	};

	return (
		<>
			<S.ListCardWrapper
				$status={status}
				testID={`receive-listCard-${item.label}`}
				onClick={selectNetworkHandler}
			>
				<ListCard.Content>
					<ListCard.Row>
						<S.ListCardRowStart>
							<S.ListCardRow>
								{item.name === NetworkType.CC2 ? (
									<ChilizIcon color={colors.icons.main.inverse} height={22} width={22} />
								) : (
									<NetworkIcon
										network={item.name as ReceiveNetworks}
										variant={item.isNotSupported ? 'disabled' : 'default'}
									/>
								)}
								<S.ListCardTitle $disabled={item.isNotSupported}>
									{item.label}
									{item.shortCode && ` (${item.shortCode})`}
								</S.ListCardTitle>
							</S.ListCardRow>
							<S.ListCardRow>
								{!item.isNotSupported ? (
									<NetworkMinimum item={item} />
								) : (
									<S.UnsupportedFlag>{$t('wallet.withdraw.networks.unsupported')}</S.UnsupportedFlag>
								)}
							</S.ListCardRow>
						</S.ListCardRowStart>
						<S.StyledRadioButton {...radioButtonProps} />
					</ListCard.Row>
				</ListCard.Content>
			</S.ListCardWrapper>
			{showAlert && (
				<S.AlertWrapper
					closable={false}
					icon={AlertIcon}
					subTitle={alertText}
					testID="withdraw networkSelecion-warning"
					title=""
					type="warning"
				/>
			)}
		</>
	);
};
namespace S {
	export const AlertWrapper = styled(Alert)`
		width: 100%;
	`;
	export const ListCardWrapper = styled(ListCard)<{
		$status?: string;
	}>`
		border: ${({ $status, theme }) =>
			$status === 'selected' ? `1px solid ${theme.colors.borders.brand.enabled}` : 'none'};
		width: 100%;
		height: auto;
		padding: ${({ theme }) => `${theme.spacings.xsmall} ${theme.spacings.medium}`};
		cursor: pointer;
	`;
	export const ListCardRowStart = styled(ListCard.Row)`
		flex-direction: column;
		align-items: flex-start;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;
	export const ListCardTitle = styled(ListCard.Title)<{ $disabled?: boolean }>`
		flex: 1;
		color: ${({ $disabled, theme }) =>
			$disabled ? theme.colors.text.main.disabled : theme.colors.text.main.enabled};
	`;
	export const ListCardSubTitle = styled(ListCard.SubTitle)`
		flex: 1;
		${({ theme }) => theme.fonts.text.text4}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;
	export const ListCardRow = styled(ListCard.Row)`
		justify-content: flex-start;
		gap: ${({ theme }) => theme.spacings.xxsmall};
		${({ theme }) => theme.fonts.text.text5};
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;
	export const ListCardAmount = styled(ListCard.Row)`
		justify-content: flex-start;
		${({ theme }) => theme.fonts.text.text5};
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;
	export const StyledRadioButton = styled(RadioButtonInput)`
		margin-bottom: 8px;
	`;
	export const UnsupportedFlag = styled.div`
		background: ${({ theme }) => theme.colors.backgrounds.destructive.enabled};
		color: ${({ theme }) => theme.colors.white};
		${({ theme }) => theme.fonts.text.text4}
		border-radius: ${({ theme }) => theme.sizing.xsmall};
		padding: ${({ theme }) => `${theme.spacings.xxxsmall} ${theme.spacings.xsmall}`};
		margin-left: ${({ theme }) => theme.spacings.xlarge};
	`;
}
